// Goal bookkeeping (§10.1, §10.2): switching goals and the shared target counts behind the hard
// rule "at most 2 bots per target" (TUNE.bot.maxPerTarget). Every goal change goes through here,
// so `counts[t]` always equals the number of brains whose pursuit goal targets bean t.
import { NO_SLOT, TUNE } from '../constants';
import { Goal, goalTargetsBean, type Brain } from './brain';
import type { Ctx } from './context';

/** Bots other than `self` already targeting `slot`. */
export function othersTargeting(ctx: Ctx, slot: number, self: Brain): number {
  const mine = goalTargetsBean(self.goal) && self.target === slot ? 1 : 0;
  return ctx.counts[slot] - mine;
}

/** True if the bot may (keep or start to) target `slot` without breaking the cap. */
export function targetFree(ctx: Ctx, slot: number, self: Brain): boolean {
  return othersTargeting(ctx, slot, self) < TUNE.bot.maxPerTarget;
}

/** Drops the brain's bean target from the counts (goal unchanged). */
export function releaseTarget(counts: number[], br: Brain): void {
  if (goalTargetsBean(br.goal) && br.target !== NO_SLOT && counts[br.target] > 0) counts[br.target]--;
}

/**
 * Switches to `goal` with bean `target` (NO_SLOT for none) and entity `ent`. A pursuit goal whose
 * target is already full is refused (returns false, nothing changes).
 */
export function setGoal(ctx: Ctx, goal: Goal, target: number, ent: number, utility: number): boolean {
  const br = ctx.brain;
  const pursuit = goalTargetsBean(goal) && target !== NO_SLOT;
  const same = br.goal === goal && br.target === target && br.targetEnt === ent;
  if (same) {
    br.goalU = utility;
    return true;
  }
  if (pursuit && !targetFree(ctx, target, br)) return false;
  releaseTarget(ctx.counts, br);
  if (br.goal !== goal) {
    br.goalSince = ctx.w.tick;
    br.pointTick = -1e9;
  }
  br.goal = goal;
  br.target = goalTargetsBean(goal) || goal === Goal.EVADE || goal === Goal.CROWN_HOLD ? target : NO_SLOT;
  br.targetEnt = ent;
  br.goalU = utility;
  br.commitAt = -1;
  br.patienceUntil = -1;
  if (goalTargetsBean(goal) && br.target !== NO_SLOT) ctx.counts[br.target]++;
  return true;
}

/** The best option found so far during a think (§10.2 utility selection). */
export interface Choice { goal: Goal; target: number; ent: number; u: number }

export function createChoice(): Choice {
  return { goal: Goal.WANDER, target: NO_SLOT, ent: 0, u: -1 };
}

/**
 * Offers one option. The current goal (same target and entity) gets the hysteresis bonus; a
 * pursuit of a full target is not eligible at all (the hard cap).
 */
export function consider(ctx: Ctx, c: Choice, goal: Goal, target: number, ent: number, u: number): void {
  const br = ctx.brain;
  if (goalTargetsBean(goal) && target !== NO_SLOT && !targetFree(ctx, target, br)) return;
  let v = u;
  if (br.goal === goal && br.target === target && br.targetEnt === ent) v *= 1 + TUNE.bot.goalHysteresis;
  if (v > c.u) {
    c.goal = goal;
    c.target = target;
    c.ent = ent;
    c.u = v;
  }
}
